
import React, { useState, useEffect } from "react";
import { BottomNavigation } from "@/components/navigation/BottomNavigation";
import { User } from "@/lib/auth";
import { format } from "date-fns";
import { toast } from "sonner";
import { WorksiteSearchSelector } from "./WorksiteSearchSelector";
import { LocationDisplay } from "./LocationDisplay";
import { TrackingControls } from "./TrackingControls";
import { BreakControls } from "./BreakControls";
import { PointageHeader } from "./PointageHeader";
import { 
  requestNotificationPermission, 
  schedulePointageReminder
} from "@/services/notificationService";
import { useIntelligentNotifications } from "@/hooks/useIntelligentNotifications";
import { usePointageLogic } from "@/hooks/usePointageLogic";

interface PointageWorkerProps {
  user: User;
}

export const PointageWorker: React.FC<PointageWorkerProps> = ({ user }) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [reminderSettings, setReminderSettings] = useState({
    morningReminder: true,
    morningReminderTime: "08:00",
    eveningReminder: true,
    eveningReminderTime: "17:00",
    smartNotifications: true,
  });
  
  const currentDate = format(new Date(), "dd/MM/yyyy");
  
  // Hook pour les notifications intelligentes
  const { updateContext, checkNotifications, scheduleNotification } = useIntelligentNotifications();
  
  // Hook pour la logique de pointage
  const {
    isTracking,
    startTime,
    selectedWorksite,
    isLoading,
    locationData,
    locationError,
    isAddressLoading,
    isOffline,
    setIsOffline,
    isOnBreak,
    breakStartTime,
    handleWorksiteChange,
    handleStartTracking: originalHandleStartTracking,
    handleEndTracking: originalHandleEndTracking,
    handleStartBreak,
    handleEndBreak,
  } = usePointageLogic(user);

  // Configurer les écouteurs de connectivité et notifications
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      toast.success("Connexion rétablie");
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      toast.warning("Mode hors-ligne activé");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    
    // Demander l'autorisation pour les notifications
    requestNotificationPermission().then(granted => {
      setNotificationsEnabled(granted);
      if (granted) {
        toast.success("Notifications intelligentes activées");
      }
    });
    
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);
  
  // Déclencher une vérification des notifications quand le contexte change
  useEffect(() => {
    if (notificationsEnabled && reminderSettings.smartNotifications) {
      updateContext();
      checkNotifications();
    }
  }, [isTracking, selectedWorksite, notificationsEnabled, reminderSettings.smartNotifications, updateContext, checkNotifications]);

  const handleReminderSettingChange = (setting: string, value: any) => {
    setReminderSettings(prev => {
      const updated = { ...prev, [setting]: value };
      localStorage.setItem("avem_reminder_preferences", JSON.stringify(updated));
      return updated;
    });
  };

  // Wrapper pour le démarrage avec notifications
  const handleStartTracking = async (comment?: string) => {
    await originalHandleStartTracking(comment);
    
    // Notification intelligente de fin de journée
    if (notificationsEnabled && reminderSettings.eveningReminder) {
      const [hours, minutes] = reminderSettings.eveningReminderTime.split(':').map(Number);
      const reminderTime = new Date();
      reminderTime.setHours(hours, minutes, 0, 0);
      const now = new Date();
      
      if (reminderTime > now) {
        const timeInMinutes = (reminderTime.getTime() - now.getTime()) / (1000 * 60);
        scheduleNotification(
          'evening-reminder',
          timeInMinutes * 60 * 1000,
          () => {
            toast.info("Rappel de fin de journée", {
              description: "N'oubliez pas de pointer la fin de votre journée de travail."
            });
          }
        );
      }
    }
  };

  // Wrapper pour la fin avec notifications
  const handleEndTracking = async (comment?: string) => {
    await originalHandleEndTracking(comment);
  };

  // Wrapper pour le début de pause avec notifications
  const handleStartBreakWithNotification = async () => {
    await handleStartBreak();
    
    // Notification intelligente de pause
    if (notificationsEnabled) {
      scheduleNotification(
        'break-reminder',
        30 * 60 * 1000, // 30 minutes
        () => {
          toast.info("Rappel de pause", {
            description: "Votre pause dure depuis 30 minutes. Pensez à la terminer si nécessaire."
          });
        }
      );
    }
  };

  // Fonction de synchronisation factice pour la compatibilité
  const handleSyncRequest = async () => {
    toast.info("Synchronisation automatique avec Supabase");
  };

  return (
    <div className="min-h-screen bg-[#F8F8F8] flex flex-col">
      {/* Header with title and settings */}
      <PointageHeader
        currentDate={currentDate}
        notificationsEnabled={notificationsEnabled}
        setNotificationsEnabled={setNotificationsEnabled}
        reminderSettings={reminderSettings}
        onReminderSettingChange={handleReminderSettingChange}
        isOffline={isOffline}
      />

      {/* Main Content */}
      <main className="flex-1 px-4 pt-16 pb-20">
        {/* Worksite Selection with Search */}
        <WorksiteSearchSelector 
          selectedWorksite={selectedWorksite} 
          onChange={handleWorksiteChange} 
          disabled={isTracking} 
        />

        {/* Location Status and Error Display */}
        <LocationDisplay 
          locationData={locationData} 
          locationError={locationError} 
          isAddressLoading={isAddressLoading}
          isOffline={isOffline} 
        />

        {/* Break Controls */}
        <BreakControls
          isTracking={isTracking}
          isOnBreak={isOnBreak}
          breakStartTime={breakStartTime}
          onStartBreak={handleStartBreakWithNotification}
          onEndBreak={handleEndBreak}
          disabled={isLoading}
        />

        {/* Time Tracking Controls */}
        <TrackingControls 
          isTracking={isTracking}
          startTime={startTime}
          isLoading={isLoading}
          hasSelectedWorksite={!!selectedWorksite}
          isOffline={isOffline}
          onStartTracking={handleStartTracking}
          onEndTracking={handleEndTracking}
          pendingSyncCount={0}
          onSyncRequest={handleSyncRequest}
        />
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation activeTab="pointage" />
    </div>
  );
};
